import { Component } from '@theme/component';
import { getViewParameterValue } from '@theme/utilities';
import { prefetch } from '@theme/plw-variant-prefetch';
import { openBackInStock } from '@theme/plw-bis-form';

/**
 * `<plw-variant-selector>` — the elevated PDP Color / Size selector.
 *
 * Presentation only: it does NOT own variant state. Each instance sits beside a
 * hidden Horizon `<fieldset>` of native option radios inside the shared
 * `<variant-picker>`. Selecting a value forwards a click to the matching radio,
 * which triggers Horizon's `variant-picker.js` (URL build + section morph).
 *
 * Two presentations of one option list:
 * - Desktop (≥750px): an attached dropdown using a `popover="manual"` panel in
 *   the top layer. The JS keeps it glued to the trigger — it repositions on
 *   scroll/resize and flips above the trigger when there's no room below — and
 *   handles dismiss (Esc + outside click) itself.
 * - Mobile (<750px): the shared `<plw-overlay>` slide-up sheet (draggable
 *   hairline handle, backdrop, focus trap), opened via its public dialog API.
 *
 * Perf (client QA 2026-08-21): colour instances prefetch the variant document
 * on hover/focus intent over an option — the same URL variant-picker.js
 * builds — into assets/plw-variant-prefetch.js, so the click morphs from
 * memory instead of waiting on a full page render.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [trigger] - The select-styled trigger button.
 * @property {HTMLElement} [panel] - The desktop popover panel.
 * @property {HTMLElement} [value] - The current-value text in the trigger.
 *
 * @extends Component<Refs>
 */
class PlwVariantSelector extends Component {
  #mobile = matchMedia('(max-width: 749px)');
  #rafId = 0;

  /** Tallest panel among the selectors in this picker row, measured at open.
   *  The flip decision uses it so ALL dropdowns in the row flip together —
   *  Color must open above whenever the taller Size panel would, even though
   *  Color alone would fit below (client QA 2026-08-10). */
  #groupMaxPanelHeight = 0;

  /** @returns {(HTMLElement & { showDialog?: () => void; closeDialog?: () => void; toggleDialog?: () => void }) | null} */
  get #overlay() {
    // Scoped to the option sheet — the size-guide overlay also nests in here.
    return this.querySelector('plw-overlay.plw-variant-selector__sheet') || this.querySelector('plw-overlay');
  }

  connectedCallback() {
    super.connectedCallback();
    this.refs.panel?.addEventListener('toggle', this.#onPanelToggle);

    // Colour rows swap the whole gallery — warm their variant document on
    // intent. (pointerover bubbles; pointerenter doesn't.)
    if (this.classList.contains('plw-variant-selector--swatch')) {
      this.addEventListener('pointerover', this.#onOptionIntent);
      this.addEventListener('focusin', this.#onOptionIntent);
    }

    // Opening the size guide from inside the desktop popover: close the
    // popover so the modal doesn't stack over it. (On mobile the guide opens
    // over the variant sheet — closing the guide returns to the sheet.)
    for (const guide of this.querySelectorAll('.plw-variant-selector__guide')) {
      guide.addEventListener('click', () => {
        if (!this.#mobile.matches) this.refs.panel?.hidePopover?.();
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.panel?.removeEventListener('toggle', this.#onPanelToggle);
    this.removeEventListener('pointerover', this.#onOptionIntent);
    this.removeEventListener('focusin', this.#onOptionIntent);
    clearTimeout(this.#intentTimer);
    this.#teardownTracking();
  }

  /* ---- Variant-document prefetch on intent ---- */

  #intentTimer = 0;

  /** @param {Event} event */
  #onOptionIntent = (event) => {
    const option = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('.plw-variant-selector__option[data-option-value-id]') : null
    );
    if (!option || option.classList.contains('is-selected')) return;

    // Debounce so a pointer sweeping down the list doesn't fetch every row.
    clearTimeout(this.#intentTimer);
    this.#intentTimer = setTimeout(() => {
      const url = this.#variantUrlFor(option);
      if (url) prefetch(url);
    }, 80);
  };

  /**
   * The URL variant-picker.js `buildRequestUrl` would build if this option were
   * selected (default product-page path: `view` param, then `option_values` =
   * the picker's current selection with this value substituted in its option
   * position). Must match that string exactly — it's the cache key.
   * @param {HTMLElement} option - The hovered/focused option button.
   * @returns {string | null}
   */
  #variantUrlFor(option) {
    const id = option.dataset.optionValueId;
    const picker = /** @type {(HTMLElement & { selectedOptionsValues?: string[] }) | null} */ (
      this.closest('variant-picker') ??
        document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`)
    );
    const radio = /** @type {HTMLElement | null} */ (picker?.querySelector(`input[data-option-value-id="${id}"]`) ?? null);
    const productUrl = picker?.dataset.productUrl;
    if (!id || !picker || !radio || !productUrl) return null;
    // Combined-listing child → the picker morphs `main` from another URL; don't cache.
    if (radio.dataset.connectedProductUrl && radio.dataset.connectedProductUrl !== productUrl) return null;

    const index = Number.parseInt(radio.dataset.fieldsetIndex ?? '', 10);
    /** @type {string[]} */
    let values;
    try {
      values = [...(picker.selectedOptionsValues ?? [])];
    } catch {
      return null;
    }
    if (Number.isNaN(index) || index >= values.length) return null;
    values[index] = id;

    const params = [];
    const view = getViewParameterValue();
    if (view) params.push(`view=${view}`);
    params.push(`option_values=${values.join(',')}`);
    return `${productUrl}?${params.join('&')}`;
  }

  /** Open/close — routes to the mobile sheet or the desktop dropdown. */
  toggle() {
    if (this.#mobile.matches) {
      this.#overlay?.toggleDialog?.();
      // The sheet has no popover `toggle` event (which keeps the desktop
      // trigger in sync) — reflect the dialog state on the trigger directly.
      queueMicrotask(() => {
        const dialog = this.#overlay?.refs?.dialog;
        const open = !!dialog?.open;
        this.refs.trigger?.setAttribute('aria-expanded', String(open));
        if (open) {
          dialog.addEventListener(
            'close',
            () => this.refs.trigger?.setAttribute('aria-expanded', 'false'),
            { once: true }
          );
        }
      });
      return;
    }
    const { panel } = this.refs;
    if (!panel) return;
    if (panel.matches(':popover-open')) {
      panel.hidePopover();
    } else {
      // Show hidden first so #position() (run on the `toggle` event) can measure
      // the real height to decide flip-up/down before the panel is painted —
      // otherwise it briefly renders below the trigger, then jumps above.
      panel.classList.add('is-positioning');
      panel.showPopover();
    }
  }

  /**
   * Forwards a panel/sheet option click to the matching hidden native radio.
   * @param {Event} event - The click event (target = the option button).
   */
  selectValue(event) {
    const option = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-option-value-id]') : null
    );
    if (!option) return;

    const id = option.dataset.optionValueId;
    // PLW (CQ-1986): a presentation-only instance (e.g. in the PDP sticky bar)
    // isn't nested in the <variant-picker>, so fall back to the page's single
    // picker for this product. `data-option-value-id` is product-globally unique,
    // so the radio lookup stays correct.
    const picker =
      this.closest('variant-picker') ??
      document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    const radio = /** @type {HTMLInputElement | null} */ (
      picker?.querySelector(`input[data-option-value-id="${id}"]`) ?? null
    );

    // Clicking the radio fires a bubbling `change` that variant-picker.js handles.
    if (radio && !radio.checked) radio.click();

    this.#close();
  }

  #close() {
    const { panel } = this.refs;
    if (panel?.matches(':popover-open')) panel.hidePopover();

    const overlay = this.#overlay;
    const dialog = overlay?.querySelector('dialog');
    if (dialog?.open) overlay?.closeDialog?.();
  }

  /**
   * Opens the back-in-stock overlay (snippets/plw-bis.liquid) for a sold-out
   * option value (CQ-1955) — the per-value "Notify me" button beside the
   * option. Selecting the value itself is a normal `selectValue` (sold-out
   * values stay selectable so shoppers can reach every colour's photography
   * even when the whole product is out of stock); this is the only path that
   * opens the capture from the selector. The Klaviyo variant id comes from the
   * hidden native radio that shares the option-value id — Liquid stamps it
   * there (`data-variant-id`) for every combination that exists as a real
   * variant.
   * @param {Event} event - The click event (target = the notify button).
   */
  notifyValue(event) {
    const button = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-option-value-id]') : null
    );
    if (!button) return;
    const id = button.dataset.optionValueId;
    const picker =
      this.closest('variant-picker') ??
      document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    const radio = /** @type {HTMLElement | null} */ (
      picker?.querySelector(`input[data-option-value-id="${id}"]`) ?? null
    );
    // No variant id means the combination doesn't exist at all — nothing to restock.
    const variantId = radio?.dataset.variantId;
    if (!variantId) return;

    this.#close();
    openBackInStock({ variantId, value: button.dataset.optionValueName ?? '' });
  }

  /* ---- Desktop dropdown positioning + dismiss ---- */

  /** @param {Event} event */
  #onPanelToggle = (event) => {
    const { trigger } = this.refs;
    const open = /** @type {ToggleEvent} */ (event).newState === 'open';
    trigger?.setAttribute('aria-expanded', String(open));
    if (open) this.#setupTracking();
    else this.#teardownTracking();
  };

  #setupTracking() {
    this.#groupMaxPanelHeight = this.#measureGroupPanels();
    this.#position();
    // Positioned with the real height — safe to reveal now (no flash).
    this.refs.panel?.classList.remove('is-positioning');
    window.addEventListener('scroll', this.#onScrollResize, { passive: true, capture: true });
    window.addEventListener('resize', this.#onScrollResize, { passive: true });
    document.addEventListener('pointerdown', this.#onOutsidePointer, true);
    document.addEventListener('keydown', this.#onKeydown);
  }

  #teardownTracking() {
    this.refs.panel?.classList.remove('is-positioning');
    window.removeEventListener('scroll', this.#onScrollResize, { capture: true });
    window.removeEventListener('resize', this.#onScrollResize);
    document.removeEventListener('pointerdown', this.#onOutsidePointer, true);
    document.removeEventListener('keydown', this.#onKeydown);
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#rafId = 0;
  }

  #onScrollResize = () => {
    if (this.#rafId) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = 0;
      this.#position();
    });
  };

  /** Close when a pointer goes down outside the panel (and outside the trigger,
   *  which manages its own toggle). */
  #onOutsidePointer = (event) => {
    const { panel, trigger } = this.refs;
    if (!panel?.matches(':popover-open')) return;
    const target = /** @type {Node} */ (event.target);
    if (panel.contains(target) || trigger?.contains(target)) return;
    panel.hidePopover();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const { panel, trigger } = this.refs;
    if (panel?.matches(':popover-open')) {
      panel.hidePopover();
      trigger?.focus();
    }
  };

  /**
   * Measures every sibling selector's panel in this picker (closed popovers
   * are display:none, so they get a hidden one-frame display override) and
   * returns the tallest — the shared flip yardstick for the row.
   */
  #measureGroupPanels() {
    const scope = this.closest('variant-picker') ?? document;
    let max = 0;
    for (const el of scope.querySelectorAll('plw-variant-selector')) {
      const panel = /** @type {PlwVariantSelector} */ (el).refs?.panel;
      if (!panel) continue;
      let height = panel.offsetHeight;
      if (!panel.matches(':popover-open')) {
        panel.style.display = 'block';
        panel.style.visibility = 'hidden';
        height = panel.offsetHeight;
        panel.style.display = '';
        panel.style.visibility = '';
      }
      max = Math.max(max, height);
    }
    return max;
  }

  /** Glue the panel to the trigger; flip above when there's no room below. */
  #position() {
    const { trigger, panel } = this.refs;
    if (!trigger || !panel) return;

    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const panelHeight = panel.offsetHeight;
    const spaceBelow = window.innerHeight - rect.bottom;
    // Decide with the row's tallest panel so both dropdowns flip together;
    // place with this panel's own height.
    const decisionHeight = Math.max(panelHeight, this.#groupMaxPanelHeight);
    const flipUp = spaceBelow < decisionHeight + gap && rect.top > spaceBelow;

    panel.style.left = `${Math.round(rect.left)}px`;
    panel.style.width = `${Math.round(rect.width)}px`;
    panel.style.top = flipUp
      ? `${Math.round(rect.top - panelHeight - gap)}px`
      : `${Math.round(rect.bottom + gap)}px`;
  }
}

if (!customElements.get('plw-variant-selector')) {
  customElements.define('plw-variant-selector', PlwVariantSelector);
}
